import React, { useState, useEffect, Dispatch, SetStateAction } from 'react';
import { Helmet } from 'react-helmet';

import './styles/ZendeskWidget.scss';

// Zendesk assigned ids for each chat department
const DEPARTMENT_IDS: ZendeskDepartmentIds = {
  SaaS: 2148338521,
  Tutoring: 2148374961,
};

const ZENDESK_SRC =
  'https://static.zdassets.com/ekr/snippet.js?key=a90fa090-077d-43c5-9db8-0a1f87d28536';

// One chat status hook (singleton) per chat "department" in Zendesk
// Currently SaaS and Tutoring
export const useSaaSZendeskChatStatus = createUseZendeskChatStatus(false);
export const useTutoringZendeskChatStatus = createUseZendeskChatStatus(false);

/** Used to load the Zendesk chat script into the document head via Helmet */
export default function ZendeskWidget({
  chatDepartment,
  zendeskSrc = ZENDESK_SRC,
}: ZendeskWidgetProps) {
  const [scriptLoaded, setScriptLoaded] = useState<boolean>(
    typeof (window as any)?.zE === 'function'
  );
  const [_, setChatOnlineStatus] =
    chatDepartment === 'SaaS' ? useSaaSZendeskChatStatus() : useTutoringZendeskChatStatus();

  // Register Zendesk event handlers
  useEffect(() => {
    if (typeof (window as any)?.zE === 'function') {
      (window as any)['zESettings'] = {
        webWidget: {
          chat: {
            departments: {
              enabled: [],
            },
          },
        },
      };
      (window as any)?.zE('webWidget:on', 'chat:connected', handleChatConnectedOrStatusChanged);
      (window as any)?.zE('webWidget:on', 'chat:status', handleChatConnectedOrStatusChanged);
      (window as any)?.zE('webWidget:on', 'chat:unreadMessages', handleUnreadMessages);
    }
  }, [scriptLoaded]);

  function handleChatConnectedOrStatusChanged() {
    const departments: Department[] | undefined = (window as any)?.zE(
      'webWidget:get',
      'chat:departments'
    );

    // Check if the given department is online in Zendesk, then set online status accordingly
    const tutoringChatOnline =
      departments?.some(
        department =>
          department.id === DEPARTMENT_IDS[chatDepartment] && department.status === 'online'
      ) ?? false;

    setChatOnlineStatus(tutoringChatOnline);
  }

  function handleUnreadMessages(unreadMessageCount: number) {
    // Force the chat window open when a new message is received from the agent
    //
    // Although this may not be perfect UX - it prevents the end user from losing
    // the chat window since we are hiding the default launcher
    if (unreadMessageCount > 0) {
      (window as any).zE('webWidget', 'open');
    }
  }

  function handleChangeClientState(_: any, addedTags: Tags) {
    if (addedTags?.scriptTags?.length) {
      const foundScript = addedTags.scriptTags.find(attributes => attributes?.src === zendeskSrc);
      if (foundScript) {
        foundScript.addEventListener('load', () => setScriptLoaded(true), { once: true });
      }
    }
  }

  return (
    // @ts-ignore onChangeClientState callback is improperly typed by DefinitelyTyped
    // onChangeClientState lets us known when a helmet managed script has been added to the dom
    <Helmet onChangeClientState={handleChangeClientState}>
      <script id="ze-snippet" src={zendeskSrc} />
    </Helmet>
  );
}

/** Opens the Zendesk chat widget even if we are outside of chat hours */
export function openWidget() {
  if (typeof (window as any)?.zE === 'function') {
    (window as any).zE('webWidget', 'open');
  }
}

function createUseZendeskChatStatus(assumeChatIsOnline: boolean) {
  let listeners: Dispatch<SetStateAction<boolean>>[] = [];
  let isChatOnline = assumeChatIsOnline;

  function setIsOnline(online: boolean) {
    isChatOnline = online;
    listeners.forEach(listener => {
      listener(online);
    });
  }

  /** Returns true or false based on whether Zendesk live chat is currently online
   * along with a setter for setting the status of live chat
   */
  function useZendeskChatStatus(): UseZendeskChatStatusReturnValues {
    const [localIsChatOnline, isChatOnlineListener] = useState<boolean>(isChatOnline);
    useEffect(() => {
      listeners.push(isChatOnlineListener);

      return () => {
        listeners = listeners.filter(listener => listener !== isChatOnlineListener);
      };
    }, []);

    return [localIsChatOnline, setIsOnline];
  }

  return useZendeskChatStatus;
}

type ZendeskChatDepartment = 'SaaS' | 'Tutoring';
type ZendeskDepartmentIds = {
  [key in ZendeskChatDepartment]: number;
};

type ZendeskWidgetProps = {
  chatDepartment: ZendeskChatDepartment;
  zendeskSrc?: string;
};

type SetStateFunc = (isLive: boolean) => void;
type UseZendeskChatStatusReturnValues = [boolean, SetStateFunc];

type Department = {
  name: string;
  status: ChatOnlineStatus;
  id: number;
};
type ChatOnlineStatus = 'online' | 'away' | 'offline';

type Tags = {
  scriptTags: HTMLScriptElement[];
};
